define(["exports", "./utils", "./xref", "./l10n", "./dfn-map", "./data-cite", "./pubsubhub"], function (_exports, _utils, _xref, _l10n, _dfnMap, _dataCite, _pubsubhub) {
  "use strict";

  Object.defineProperty(_exports, "__esModule", {
    value: true
  });
  _exports.run = run;
  _exports.name = void 0;
  // @ts-check
  // Module core/link-to-dfn
  // Gives definitions in conf.definitionMap IDs and links <a> tags
  // to the matching definitions.
  const name = "core/link-to-dfn";
  _exports.name = name;
  const l10n = {
    en: {
      /**
       * @param {string} title
       */
      duplicateMsg(title) {
        return "Duplicate definition(s) of '".concat(title, "'");
      },

      duplicateTitle: "This is defined more than once in the document."
    }
  };
  const lang = _l10n.lang in l10n ? _l10n.lang : "en";

  class CaseInsensitiveMap extends Map {
    /**
     * @param {Array<[String, HTMLElement]>} [entries]
     */
    constructor(entries = []) {
      super();
      entries.forEach(([key, elem]) => {
        this.set(key, elem);
      });
      return this;
    }

    set(key, elem) {
      super.set(key.toLowerCase(), elem);
      return this;
    }

    get(key) {
      return super.get(key.toLowerCase());
    }

    has(key) {
      return super.has(key.toLowerCase());
    }

    delete(key) {
      return super.delete(key.toLowerCase());
    }

  }

  async function run(conf) {
    const titleToDfns = mapTitleToDfns();
    /** @type {HTMLElement[]} */

    const possibleExternalLinks = [];
    /** @type {HTMLAnchorElement[]} */

    const badLinks = [];
    const localLinkSelector = "a[data-cite=''], a:not([href]):not([data-cite]):not(.logo):not(.externalDFN)";
    document.querySelectorAll(localLinkSelector).forEach(
    /** @type {HTMLAnchorElement} */
    anchor => {
      const linkTargets = (0, _utils.getLinkTargets)(anchor);
      const foundDfn = linkTargets.some(target => {
        return findLinkTarget(target, anchor, titleToDfns, possibleExternalLinks);
      });

      if (!foundDfn && linkTargets.length !== 0) {
        if (anchor.dataset.cite === "") {
          badLinks.push(anchor);
        } else {
          possibleExternalLinks.push(anchor);
        }
      }
    });
    showLinkingError(badLinks);

    if (conf.xref) {
      possibleExternalLinks.push(...findExplicitExternalLinks());

      try {
        await (0, _xref.run)(conf, possibleExternalLinks);
      } catch (error) {
        console.error(error);
        showLinkingError(possibleExternalLinks);
      }
    } else {
      showLinkingError(possibleExternalLinks);
    }

    await (0, _dataCite.linkInlineCitations)(document, conf); // Added message for legacy compat with Aria specs
    // See https://github.com/w3c/respec/issues/793

    (0, _pubsubhub.pub)("end", "core/link-to-dfn");
  }

  function mapTitleToDfns() {
    const titleToDfns = new CaseInsensitiveMap();
    Object.keys(_dfnMap.definitionMap).forEach(title => {
      const {
        result,
        duplicates
      } = collectDfns(title);
      titleToDfns.set(title, result);

      if (duplicates.length > 0) {
        (0, _utils.showInlineError)(duplicates, l10n[lang].duplicateMsg(title), l10n[lang].duplicateTitle);
      }
    });
    return titleToDfns;
  }
  /**
   * @param {string} title
   */


  function collectDfns(title) {
    /** @type {Map<string, HTMLElement>} */
    const result = new Map();
    const duplicates = [];

    _dfnMap.definitionMap[title].forEach(dfn => {
      const {
        dfnFor = ""
      } = dfn.dataset;

      if (result.has(dfnFor)) {
        // We want <dfn> definitions to take precedence over
        // definitions from WebIDL. WebIDL definitions wind
        // up as <span>s instead of <dfn>.
        const oldIsDfn = result.get(dfnFor).localName === "dfn";
        const newIsDfn = dfn.localName === "dfn";

        if (oldIsDfn) {
          if (!newIsDfn) {
            // Don't overwrite <dfn> definitions.
            return;
          }

          duplicates.push(dfn);
        }
      }

      result.set(dfnFor, dfn);
      (0, _utils.addId)(dfn, "dfn", title);
    });

    return {
      result,
      duplicates
    };
  }
  /**
   * @param {import("./utils.js").LinkTarget} target
   * @param {HTMLAnchorElement} anchor
   * @param {CaseInsensitiveMap} titleToDfns
   * @param {HTMLElement[]} possibleExternalLinks
   */


  function findLinkTarget(target, anchor, titleToDfns, possibleExternalLinks) {
    const {
      linkFor
    } = anchor.dataset;

    if (!titleToDfns.has(target.title) || !titleToDfns.get(target.title).get(target.for)) {
      return false;
    }

    const dfn = titleToDfns.get(target.title).get(target.for);

    if (dfn.dataset.cite) {
      anchor.dataset.cite = dfn.dataset.cite;
    } else if (linkFor && !titleToDfns.get(linkFor)) {
      possibleExternalLinks.push(anchor);
    } else if (dfn.classList.contains("externalDFN")) {
      // data-lt[0] serves as unique id for the dfn which this element references
      const lt = dfn.dataset.lt ? dfn.dataset.lt.split("|") : [];
      anchor.dataset.lt = lt[0] || dfn.textContent;
      possibleExternalLinks.push(anchor);
    } else {
      if (anchor.dataset.idl === "partial") {
        possibleExternalLinks.push(anchor);
      } else {
        anchor.href = "#".concat(dfn.id);
        anchor.classList.add("internalDFN");
      }
    }

    if (!anchor.hasAttribute("data-link-type")) {
      anchor.dataset.linkType = "idl" in dfn.dataset ? "idl" : "dfn";
    }

    if (isCode(dfn)) {
      wrapAsCode(anchor, dfn);
    }

    return true;
  }
  /**
   * Check if a definition is a code
   * @param {HTMLElement} dfn a definition
   */


  function isCode(dfn) {
    if (dfn.closest("code,pre")) {
      return true;
    } // Note that childNodes.length === 1 excludes
    // definitions that have either other text, or other
    // whitespace, inside the <dfn>.


    if (dfn.childNodes.length !== 1) {
      return false;
    }

    const [first] =
    /** @type {NodeListOf<HTMLElement>} */
    dfn.childNodes;
    return first.localName === "code";
  }
  /**
   * Wrap links by <code>.
   * @param {HTMLAnchorElement} ant a link
   * @param {HTMLElement} dfn a definition
   */


  function wrapAsCode(ant, dfn) {
    // only add code to IDL when the definition matches
    const term = ant.textContent.trim();
    const isIDL = dfn.dataset.hasOwnProperty("idl");
    const needsCode = shouldWrapByCode(dfn, term);

    if (!isIDL || needsCode) {
      (0, _utils.wrapInner)(ant, document.createElement("code"));
    }
  }
  /**
   * @param {HTMLElement} dfn
   * @param {string} term
   */


  function shouldWrapByCode(dfn, term) {
    const {
      dataset
    } = dfn;

    if (dfn.textContent.trim() === term) {
      return true;
    } else if (dataset.title === term) {
      return true;
    } else if (dataset.lt) {
      return dataset.lt.split("|").includes(term);
    }

    return false;
  }
  /**
   * Find additional references that need to be looked up externally.
   * Examples: a[data-cite="spec"], dfn[data-cite="spec"], dfn.externalDFN
   */


  function findExplicitExternalLinks() {
    /** @type {NodeListOf<HTMLElement>} */
    const links = document.querySelectorAll("a[data-cite]:not([data-cite='']):not([data-cite*='#']), " + "dfn[data-cite]:not([data-cite='']):not([data-cite*='#'])");
    /** @type {NodeListOf<HTMLElement>} */

    const externalDFNs = document.querySelectorAll("dfn.externalDFN");
    return [...links].filter(el => {
      // ignore empties
      if (el.textContent.trim() === "") return false;
      /** @type {HTMLElement} */

      const closest = el.closest("[data-cite]");
      return !closest || closest.dataset.cite !== "";
    }).concat(...externalDFNs);
  }

  function showLinkingError(elems) {
    elems.forEach(elem => {
      (0, _utils.showInlineWarning)(elem, "Found linkless `<a>` element with text \"".concat(elem.textContent, "\" but no matching `<dfn>`"), "Linking error: not matching `<dfn>`");
    });
  }
});
//# sourceMappingURL=link-to-dfn.js.map